import { Box, Button, Card, Chip, Dialog, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import useOpenable from '../../hooks/useOpenable';
import { orderAtom } from '../state/state';
import { currentContextAtom } from '../../state';
import { isOrderSuggestedByStaff } from '../../common/utils/order.utils';
import OrderHeader from './OrderHeader';
import OrderDeliveryDateForm from './OrderDeliveryDateForm';
import OrderCreationInfo from './OrderCreationInfo';
import OrderSuggestorInfo from './OrderSuggestorInfo';
import ReorderButton from './ReorderButton';
import OrderSuggestionChip from './OrderSuggestionChip';
import OrderAdditionalInformation from './OrderAdditionalInformation';
import OrderExportButton from './OrderExportButton';
import Comments from './comments/Comments';

export default function OrderInformation(): React.JSX.Element {
  const { t } = useTranslation(['common', 'order']);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'));
  const { open, close, isOpen } = useOpenable();
  const order = useAtomValue(orderAtom);
  const { realm } = useAtomValue(currentContextAtom);
  const isKheopsSuggestion = realm === Business_Profile_Enum.Supplier && isOrderSuggestedByStaff(order);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3,
        [theme.breakpoints.up('lg')]: {          
          position: 'sticky',
          top: '96px',
          px: 0,
          pt: 0,
        },
      }}
    >
      <OrderHeader compact={isSmall} />
      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'flex',
          },
          px: {
            xs: 0,
            lg: 2,
          },
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ReorderButton type="chip" orderReferenceId={order.reference_id} />
          <OrderExportButton
            orderReferenceId={order.reference_id}
            orderFriendlyId={order.friendly_id}
            orderId={order.id}
            variant="chip"
          />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 2 }}>
          <OrderCreationInfo />
          <OrderDeliveryDateForm />
          {order.suggestor && <OrderSuggestorInfo suggestor={order.suggestor} />}
        </Box>
        <OrderAdditionalInformation />
      </Box>
      {isSmall && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <ReorderButton type="chip" orderReferenceId={order.reference_id} />
          <OrderExportButton
            orderReferenceId={order.reference_id}
            orderFriendlyId={order.friendly_id}
            orderId={order.id}
            variant="chip"
          />
          <Comments compact />
          <Chip
            variant="assistive"
            onClick={open}
            icon={<InfoOutlinedIcon />}
            label={t('common:more_information')}
          />
          <Dialog
            open={isOpen}
            onClose={close}
            slotProps={{
              paper: {
                sx: {
                  p: 2,
                  m: 1,
                  gap: 2,
                  width: '100%',
                  minHeight: 620,
                  boxSizing: 'border-box',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="titleMedium">
                {t('order:order_details')}
              </Typography>
              <Button variant="text" onClick={close}>
                <CloseIcon />
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 2 }}>
                <OrderCreationInfo />
                <OrderDeliveryDateForm />
                {order.suggestor && <OrderSuggestorInfo suggestor={order.suggestor} />}
              </Box>
              {order.suggestor_id && (
                <Box>
                  <OrderSuggestionChip isKheopsSuggestion={isKheopsSuggestion} />
                </Box>
              )}
              <OrderAdditionalInformation compact />
            </Box>
          </Dialog>
        </Box>
      )}
    </Card>
  );
}
